import {
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { Input } from '@/lib/base/forms/input/input';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { useCreateSavedFilterView } from '@/lib/crm/list/hooks/useSavedFilterViews';
import { logger } from '@/lib/logger';
import type {
  SavedFilterDefinition,
  SavedFilterView,
  SavedFilterViewState,
} from '@/types/savedFilterView';

export interface SaveCurrentViewModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  orgId: number;
  /** The entity type the view is being saved for; passed straight into the POST body. */
  entityType: string;
  /**
   * Filter+column+sort snapshot computed by the parent at the moment the user
   * opened the modal. The modal does not snapshot — it just submits.
   */
  payload: {
    filter_definition: SavedFilterDefinition;
    view_state: SavedFilterViewState;
  };
  /** Optional callback fired after a successful save. The parent can refocus the picker trigger or show a toast. */
  onSaved?: (view: SavedFilterView) => void;
  /** Human-readable org name, used to label the share checkbox ("Share with everyone in {orgName}"). */
  orgName?: string;
  /**
   * When provided, renders a "Set as default view" checkbox; if omitted the
   * checkbox does not render, so a surface that supports defaults MUST pass
   * this or the entry point silently disappears.
   */
  onSetAsDefault?: (view: SavedFilterView) => void;
}

export function SaveCurrentViewModal({
  isOpen,
  onOpenChange,
  orgId,
  entityType,
  payload,
  onSaved,
  orgName,
  onSetAsDefault,
}: SaveCurrentViewModalProps) {
  const [name, setName] = useState('');
  const [isSharedWithOrg, setIsSharedWithOrg] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formId = useId();

  useEffect(() => {
    if (isOpen) {
      setName('');
      setIsSharedWithOrg(false);
      setSetAsDefault(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  const { mutateAsync, isPending } = useCreateSavedFilterView(
    orgId,
    entityType
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setSubmitError('Name is required');
        return;
      }
      if (isPending) {
        return;
      }
      try {
        const view = await mutateAsync({
          name: trimmed,
          target_entity_type: entityType,
          filter_definition: payload.filter_definition,
          view_state: payload.view_state,
          is_shared_with_org: isSharedWithOrg,
        });
        onSaved?.(view);
        if (setAsDefault) {
          onSetAsDefault?.(view);
        }
        onOpenChange(false);
      } catch (err) {
        logger.error(
          'Error saving current view',
          err instanceof Error ? err : new Error(String(err))
        );
        setSubmitError("We couldn't save this view. Try again.");
      }
    },
    [
      name,
      isPending,
      mutateAsync,
      entityType,
      payload,
      isSharedWithOrg,
      setAsDefault,
      onSaved,
      onSetAsDefault,
      onOpenChange,
    ]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const shareLabel = orgName
    ? `Share with everyone in ${orgName}`
    : 'Share with team';

  const isNameEmpty = !name.trim();

  const footer = useMemo(
    () => (
      <div className='flex justify-end gap-2'>
        <Button color='tertiary' onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          form={formId}
          type='submit'
          color='primary'
          isDisabled={isPending || isNameEmpty}
        >
          {isPending ? 'Saving…' : 'Save view'}
        </Button>
      </div>
    ),
    [formId, handleCancel, isPending, isNameEmpty]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-md'>
        <Dialog title='Save current view' footer={footer}>
          <form
            id={formId}
            onSubmit={handleSubmit}
            className='flex flex-col gap-4 py-2'
          >
            <Input
              label='Name'
              isRequired
              value={name}
              onChange={setName}
              placeholder='e.g. My open contacts'
              autoFocus
              isInvalid={!!submitError && isNameEmpty}
              hint={submitError && isNameEmpty ? submitError : undefined}
            />
            <Checkbox
              isSelected={isSharedWithOrg}
              onChange={setIsSharedWithOrg}
              label={shareLabel}
            />
            {onSetAsDefault && (
              <Checkbox
                isSelected={setAsDefault}
                onChange={setSetAsDefault}
                label='Set as default view'
              />
            )}
            {submitError && !isNameEmpty ? (
              <p className='text-sm text-fg-error'>{submitError}</p>
            ) : null}
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
